import React, { createContext, useContext, useState, useEffect } from "react";
import { FIREBASE_AUTH, FIREBASE_DB } from "../firebase.config";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const verifyAdminRole = async (userId) => {
    try {
      const userDocRef = doc(FIREBASE_DB, "users", userId);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        console.error("User document does not exist in Firestore");
        return false;
      }
      const userData = userDoc.data();
      console.log('User data from Firestore:', userData);
      return userData.role === "admin";
    } catch (error) {
      console.error("Error verifying admin role:", error);
      return false;
    }
  };

  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(
        FIREBASE_AUTH,
        email,
        password
      );
      const user = userCredential.user;

      // Force refresh to get latest email verification status
      await user.reload();

      // Get user data from Firestore
      const userDoc = await getDoc(doc(FIREBASE_DB, "users", user.uid));
      const userData = userDoc.data();
      
      const cleanUser = {
        uid: user.uid,
        email: user.email,
        emailVerified: FIREBASE_AUTH.currentUser.emailVerified,
        role: userData?.role || "user",
        name: userData?.name
      };

      console.log('Setting current user after login:', cleanUser);
      setCurrentUser(cleanUser);
      return cleanUser;
    } catch (error) {
      console.error("Login error:", error);
      if (error.code === "auth/invalid-credential") {
        throw new Error("Invalid email or password. Please try again.");
      } else {
        throw new Error(
          "An error occurred during login. Please try again later."
        );
      }
    }
  }

  function logout() {
    return signOut(FIREBASE_AUTH).then(() => {
      setCurrentUser(null);
    });
  }

  async function saveJob(jobId) {
    if (!currentUser) {
      throw new Error("You must be logged in to save a job");
    }
    const savedJobRef = doc(
      FIREBASE_DB,
      "savedJobs",
      `${currentUser.uid}_${jobId}`
    );
    await setDoc(savedJobRef, {
      userId: currentUser.uid,
      jobId: jobId,
      savedAt: new Date(),
    });
  }

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(FIREBASE_AUTH, email);
      return true;
    } catch (error) {
      console.error("Password reset error:", error);
      throw error;
    }
  };

  const checkEmailVerification = async () => {
    try {
      if (FIREBASE_AUTH.currentUser) {
        // Force refresh the user to get the latest email verification status
        await FIREBASE_AUTH.currentUser.reload();
        const isAdmin = await verifyAdminRole(FIREBASE_AUTH.currentUser.uid);
        
        const cleanUser = {
          uid: FIREBASE_AUTH.currentUser.uid,
          email: FIREBASE_AUTH.currentUser.email,
          emailVerified: FIREBASE_AUTH.currentUser.emailVerified,
          role: isAdmin ? "admin" : "employee"
        };
        
        setCurrentUser(cleanUser);
        
        return FIREBASE_AUTH.currentUser.emailVerified;
      }
      return false;
    } catch (error) {
      console.error("Error checking email verification:", error);
      return false;
    }
  };

  const sendVerificationEmail = async () => {
    try {
      if (FIREBASE_AUTH.currentUser && !FIREBASE_AUTH.currentUser.emailVerified) {
        await FIREBASE_AUTH.currentUser.sendEmailVerification();
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error sending verification email:", error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(FIREBASE_AUTH, async (user) => {
      try {
        if (user) {
          // Force refresh the token to get latest email verification status
          await user.reload();
          
          // Get user data from Firestore first
          const userDoc = await getDoc(doc(FIREBASE_DB, "users", user.uid));
          const userData = userDoc.data();
          console.log('User data from Firestore:', userData);

          const cleanUser = {
            uid: user.uid,
            email: user.email,
            emailVerified: FIREBASE_AUTH.currentUser.emailVerified,
            role: userData?.role || "user",
            name: userData?.name
          };
          
          console.log('Setting current user with Firestore data:', cleanUser);
          setCurrentUser(cleanUser);
        } else {
          setCurrentUser(null);
        }
      } catch (error) {
        console.error("Error in auth state change:", error);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    login,
    logout,
    resetPassword,
    saveJob,
    loading,
    checkEmailVerification,
    sendVerificationEmail
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

// Export the context and provider separately
export { AuthContext };
export default AuthProvider;
